import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activated, setActivated] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setErrorMessage("Lien d'activation invalide — aucun token fourni.");
      setLoading(false);
      return;
    }
    const validateToken = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/activate/info?token=${token}`);
        if (res.ok) {
          const data = await res.json();
          setTokenValid(true);
          setUserInfo(data);
        } else {
          const err = await res.json().catch(() => ({}));
          setErrorMessage(err.detail || "Ce lien d'activation est invalide ou a expiré.");
        }
      } catch {
        setErrorMessage("Impossible de vérifier le lien. Veuillez réessayer.");
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErrorMessage('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Les mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (res.ok) {
        setActivated(true);
      } else {
        const err = await res.json().catch(() => ({}));
        setErrorMessage(err.detail || "Erreur lors de l'activation.");
      }
    } catch {
      setErrorMessage("Erreur réseau. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a2e] px-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#9398DE] animate-spin mx-auto mb-4" />
          <p className="text-white/70">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (activated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a2e] px-4">
        <Card className="w-full max-w-md bg-[#272727] border-0 text-white">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Compte activé !</h2>
            <p className="text-white/70 mb-6">
              Bienvenue {userInfo?.firstName} ! Votre compte est prêt.
              Vous pouvez maintenant vous connecter.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#9398DE] hover:bg-[#7b80c9] text-white font-semibold py-3 rounded-xl"
            >
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a2e] px-4">
        <Card className="w-full max-w-md bg-[#272727] border-0 text-white">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
            <p className="text-white/70 mb-6">{errorMessage}</p>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Activation form
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1a2e] px-4">
      <Card className="w-full max-w-md bg-[#272727] border-0 text-white">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#9398DE]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#9398DE]" />
          </div>
          <CardTitle className="text-2xl font-bold">
            Bonjour {userInfo?.firstName} !
          </CardTitle>
          <p className="text-white/60 mt-2">
            Créez votre mot de passe pour activer votre compte
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-white/70 mb-1">Email</label>
              <Input
                value={userInfo?.email || ''}
                disabled
                className="bg-[#333] border-0 text-white/50"
              />
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-1">Mot de passe</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  className="bg-[#333] border-0 text-white pr-10"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-white/70 mb-1">Confirmer le mot de passe</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez votre mot de passe"
                className="bg-[#333] border-0 text-white"
                required
                minLength={6}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Les mots de passe ne correspondent pas</p>
              )}
            </div>

            {errorMessage && (
              <p className="text-sm text-red-400 text-center">{errorMessage}</p>
            )}

            <Button
              type="submit"
              disabled={submitting || password.length < 6 || password !== confirmPassword}
              className="w-full bg-[#9398DE] hover:bg-[#7b80c9] text-white font-semibold py-3 rounded-xl disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Activation...
                </>
              ) : (
                'Activer mon compte'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
